import React, { useState, useEffect } from 'react';
import { AutoComplete, Spin } from 'antd';
import debounce from 'lodash/debounce';

const MAPBOX_ACCESS_TOKEN = "pk.eyJ1IjoiZGVjYWRhbmNlYXBwIiwiYSI6ImNtYWZqZGpnbjAzYmIya3Bsemg2MDl3NDMifQ.rX911Pl-UNAhoOMPZPLWBw";

const AddressInput = ({ value, onChange, autoSelect = false, country = null }) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize from form value
  useEffect(() => {
    console.log('AddressInput received value:', value);
    
    // Handle both direct address and localisation object formats
    const address = value?.localisation?.address || value?.address || '';
    console.log('Setting initial address:', address);
    
    if (address && !hasInitialized) {
      setInputValue(address);
      setHasInitialized(true);
      // Only search for coordinates on initial load if autoSelect is true
      if (autoSelect) {
        searchAddress(address, true);
      }
    }
  }, [value, autoSelect, hasInitialized]);

  const searchAddress = debounce(async (searchText, isInitialLoad = false) => {
    if (!searchText || searchText.length < 3) {
      setOptions([]);
      return;
    }
    console.log('Searching address:', searchText, 'isInitialLoad:', isInitialLoad);
    setLoading(true);
    try {
      const countryParam = country ? `&country=${country}` : '';
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchText)}.json?access_token=${MAPBOX_ACCESS_TOKEN}${countryParam}&autocomplete=true&limit=5`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch addresses');
      const data = await response.json();
      console.log('Mapbox response:', data);
      
      const formattedOptions = (data.features || []).map((feature) => ({
        value: feature.place_name,
        label: feature.place_name,
        coordinates: feature.center,
        rawFeature: feature
      }));
      setOptions(formattedOptions);

      // Only auto-select if autoSelect is enabled and it's the initial load
      if (autoSelect && isInitialLoad && formattedOptions.length > 0) {
        console.log('Auto-selecting first result:', formattedOptions[0]);
        handleSelect(formattedOptions[0].value, formattedOptions[0]);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, 600);

  const handleSelect = (selectedValue, option) => {
    console.log('Selected address:', selectedValue, option);
    setInputValue(selectedValue);
    
    if (onChange) {
      const newValue = {
        localisation: {
          coordinates: option.coordinates,
          address: selectedValue
        }
      };
      console.log('Calling onChange with:', newValue);
      onChange(newValue);
    }
  };

  const handleChange = (newValue) => {
    console.log('Input changed:', newValue);
    setInputValue(newValue);
    searchAddress(newValue);
  };

  return (
    <AutoComplete
      value={inputValue}
      options={options}
      onSearch={searchAddress}
      onChange={handleChange}
      onSelect={handleSelect}
      notFoundContent={loading ? <Spin size="small" /> : null}
      style={{ width: '100%' }}
        placeholder="Enter address (street, city, etc.)"
      />
  );
};

export default AddressInput; 